/**
 * Test Fit — per-type preferences sub-form (Workflow Prompt 2).
 * Required: targetFootprintAcres (sets the buildable area target).
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import type { TestFitPreferences } from '../../../types/siteRag';
import { FieldRow } from './shared';
import { validateTestFitPreferences } from '../../../lib/reportPreferences/validation';
// Pure validator lives in lib so server code can use it without this form.
export { validateTestFitPreferences };

interface Props {
  value: TestFitPreferences | undefined;
  onChange: (next: TestFitPreferences) => void;
}

export const TestFitPreferencesForm: React.FC<Props> = ({ value: rawValue, onChange }) => {
  // PB2: belt-and-braces.
  const value: TestFitPreferences = (rawValue ?? {}) as TestFitPreferences;
  const set = <K extends keyof TestFitPreferences>(key: K, v: TestFitPreferences[K]) => {
    onChange({ ...value, [key]: v });
  };
  const isPhased = value.phasing === 'phased';

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FieldRow label="Target footprint (acres)" fieldKey="targetFootprintAcres" required help="The acreage target for the campus. Drives net-buildable-area calculations and phasing.">
          <Input
            type="number" min={1} step={0.5} placeholder="e.g. 50"
            value={value.targetFootprintAcres ?? ''}
            onChange={(e) => set('targetFootprintAcres', e.target.value === '' ? undefined : Number(e.target.value))}
            className="h-9 text-sm"
          />
        </FieldRow>

        <FieldRow label="Phasing" fieldKey="phasing" help="Single = one buildout. Phased = multiple deployment phases with infrastructure pre-positioned.">
          <Select value={value.phasing ?? ''} onValueChange={(v) => set('phasing', v as TestFitPreferences['phasing'])}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single buildout</SelectItem>
              <SelectItem value="phased">Phased</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Phase count" fieldKey="phaseCount" required={isPhased} help="Number of phases. Required when phasing = Phased; ignored otherwise.">
          <Input
            type="number" min={2} step={1} placeholder={isPhased ? 'e.g. 3' : 'N/A — single buildout'}
            disabled={!isPhased}
            value={value.phaseCount ?? ''}
            onChange={(e) => set('phaseCount', e.target.value === '' ? undefined : Number(e.target.value))}
            className="h-9 text-sm"
          />
        </FieldRow>

        <FieldRow label="Setback override (ft)" fieldKey="setbackOverrideFt" help="Override the default municipal setback. Leave blank to use the zoning default.">
          <Input
            type="number" min={0} step={5} placeholder="e.g. 50"
            value={value.setbackOverrideFt ?? ''}
            onChange={(e) => set('setbackOverrideFt', e.target.value === '' ? undefined : Number(e.target.value))}
            className="h-9 text-sm"
          />
        </FieldRow>

        <FieldRow label="Parking ratio (per employee)" fieldKey="parkingRatioPerEmployee" help="Stalls per FTE. Lights-out facilities use ~0.5; staffed colos use 1.5–2.0.">
          <Input
            type="number" min={0} step={0.1} placeholder="e.g. 1.5"
            value={value.parkingRatioPerEmployee ?? ''}
            onChange={(e) => set('parkingRatioPerEmployee', e.target.value === '' ? undefined : Number(e.target.value))}
            className="h-9 text-sm"
          />
        </FieldRow>

        <FieldRow label="Stormwater detention (gal)" fieldKey="stormwaterDetentionGal" help="Required detention volume in gallons. If blank, the analysis estimates from impervious area + rational method.">
          <Input
            type="number" min={0} step={1000} placeholder="e.g. 250000"
            value={value.stormwaterDetentionGal ?? ''}
            onChange={(e) => set('stormwaterDetentionGal', e.target.value === '' ? undefined : Number(e.target.value))}
            className="h-9 text-sm"
          />
        </FieldRow>
      </div>
    </div>
  );
};
